import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Radio } from '@packages/shared/src/ui/Radio/Radio';
import { RadioGroup } from '@packages/shared/src/ui/RadioGroup/RadioGroup';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { Controller, useFormContext } from 'react-hook-form';
import { generalFormMessages, furtherInformationMessages } from './messages';
import type { CreditCheckFormValues } from './schema';
import type { OpenDialogHandler } from './types';

type LongFormSectionProps = {
  handleOpenDialog: OpenDialogHandler;
};
export const LongFormSection: React.FC<LongFormSectionProps> = ({ handleOpenDialog }) => {
  const {
    resetField,
    formState: { errors },
    watch,
    getValues,
    control,
  } = useFormContext<CreditCheckFormValues<'long'>>();

  const { formatMessage } = useIntl();
  const hasAutomobile = watch('secondPartForm.automobileQuestion') === 'yes';
  useEffect(() => {
    if (!hasAutomobile) {
      resetField('secondPartForm.automobileFollowUpQuestion');
    }
  }, [hasAutomobile, resetField]);
  const childrenStatus = Number(getValues('firstPartForm.numberOfChildren'));
  return (
    <div data-testid="long-form-section" className="flex flex-col gap-1.5">
      <Controller
        control={control}
        name="secondPartForm.savingsRetirementQuestion"
        render={({ field }) => (
          <RadioGroup
            {...field}
            label={formatMessage(furtherInformationMessages.savingsRetirementQuestion, {
              // display the asterisk in the label, but not in the dialog title
              asterisk: (chunks) => chunks,
            }).toString()}
            error={!!errors?.secondPartForm?.savingsRetirementQuestion?.message}
            notificationText={errors?.secondPartForm?.savingsRetirementQuestion?.message}
            id="savingsRetirementQuestion"
          >
            <Radio
              value="yes"
              label={formatMessage(generalFormMessages.yesResponse)}
              name="savingsRetirementQuestion"
              error={!!errors.thirdPartForm?.creditContractQuestion?.message}
              onChange={field.onChange}
              checked={field.value === 'yes'}
            />
            <Radio
              value="no"
              label={formatMessage(generalFormMessages.noResponse)}
              name="savingsRetirementQuestion"
              error={!!errors.thirdPartForm?.creditContractQuestion?.message}
              onChange={field.onChange}
              checked={field.value === 'no'}
            />
          </RadioGroup>
        )}
      />
      <Button
        onClick={(e) =>
          handleOpenDialog(
            furtherInformationMessages.savingsRetirementQuestion,
            furtherInformationMessages.savingsRetirmentDialogText,
            e,
          )
        }
        variant="link"
        size="base"
        className="text-size-body3 items-center justify-start px-0 py-0"
      >
        {formatMessage(furtherInformationMessages.furtherInformationCardLink)}
      </Button>
      <Controller
        control={control}
        name="secondPartForm.automobileQuestion"
        render={({ field }) => (
          <RadioGroup
            {...field}
            label={formatMessage(furtherInformationMessages.automobileQuestion, {
              // display the asterisk in the label, but not in the dialog title
              asterisk: (chunks) => chunks,
            }).toString()}
            error={!!errors?.secondPartForm?.automobileQuestion?.message}
            notificationText={errors?.secondPartForm?.automobileQuestion?.message}
          >
            <Radio
              value="yes"
              label={formatMessage(generalFormMessages.yesResponse)}
              error={!!errors.thirdPartForm?.creditContractQuestion?.message}
              onChange={field.onChange}
              name="automobileQuestion"
              checked={field.value === 'yes'}
            />
            <Radio
              value="no"
              label={formatMessage(generalFormMessages.noResponse)}
              error={!!errors.thirdPartForm?.creditContractQuestion?.message}
              onChange={field.onChange}
              name="automobileQuestion"
              checked={field.value === 'no'}
            />
          </RadioGroup>
        )}
      />
      <Button
        onClick={(e) =>
          handleOpenDialog(
            furtherInformationMessages.automobileQuestion,
            furtherInformationMessages.automobileDialogText,
            e,
          )
        }
        variant="link"
        size="base"
        className="text-size-body3 items-center justify-start px-0 py-0"
      >
        {formatMessage(furtherInformationMessages.furtherInformationCardLink)}
      </Button>
      {hasAutomobile && (
        <>
          <Controller
            control={control}
            name="secondPartForm.automobileFollowUpQuestion"
            render={({ field }) => (
              <RadioGroup
                {...field}
                label={formatMessage(furtherInformationMessages.automobileFollowUpQuestion, {
                  // display the asterisk in the label, but not in the dialog title
                  asterisk: (chunks) => chunks,
                }).toString()}
                error={!!errors?.secondPartForm?.automobileFollowUpQuestion?.message}
                notificationText={errors?.secondPartForm?.automobileFollowUpQuestion?.message}
              >
                <Radio
                  value="yes"
                  label={formatMessage(generalFormMessages.yesResponse)}
                  error={!!errors.thirdPartForm?.creditContractQuestion?.message}
                  onChange={field.onChange}
                  name="automobileFollowUpQuestion"
                  checked={field.value === 'yes'}
                />
                <Radio
                  value="no"
                  label={formatMessage(generalFormMessages.noResponse)}
                  error={!!errors.thirdPartForm?.creditContractQuestion?.message}
                  onChange={field.onChange}
                  name="automobileFollowUpQuestion"
                  checked={field.value === 'no'}
                />
              </RadioGroup>
            )}
          />
          <Button
            onClick={(e) =>
              handleOpenDialog(
                furtherInformationMessages.automobileFollowUpQuestion,
                furtherInformationMessages.automobileFollowUpDialogText,
                e,
              )
            }
            variant="link"
            size="base"
            className="text-size-body3 items-center justify-start px-0 py-0"
          >
            {formatMessage(furtherInformationMessages.furtherInformationCardLink)}
          </Button>
        </>
      )}
      {childrenStatus > 0 && (
        <>
          <Controller
            control={control}
            name="secondPartForm.childDayCareQuestion"
            render={({ field }) => (
              <RadioGroup
                {...field}
                label={formatMessage(furtherInformationMessages.childDayCareQuestion, {
                  // display the asterisk in the label, but not in the dialog title
                  asterisk: (chunks) => chunks,
                }).toString()}
                error={!!errors?.secondPartForm?.childDayCareQuestion?.message}
                notificationText={errors?.secondPartForm?.childDayCareQuestion?.message}
              >
                <Radio
                  value="yes"
                  label={formatMessage(generalFormMessages.yesResponse)}
                  error={!!errors.thirdPartForm?.creditContractQuestion?.message}
                  onChange={field.onChange}
                  name="childDayCareQuestion"
                  checked={field.value === 'yes'}
                />
                <Radio
                  value="no"
                  label={formatMessage(generalFormMessages.noResponse)}
                  error={!!errors.thirdPartForm?.creditContractQuestion?.message}
                  onChange={field.onChange}
                  name="childDayCareQuestion"
                  checked={field.value === 'no'}
                />
              </RadioGroup>
            )}
          />
          <Button
            onClick={(e) =>
              handleOpenDialog(
                furtherInformationMessages.childDayCareQuestion,
                furtherInformationMessages.childDayCareDialogText,
                e,
              )
            }
            variant="link"
            size="base"
            className="text-size-body3 items-center justify-start px-0 py-0"
          >
            {formatMessage(furtherInformationMessages.furtherInformationCardLink)}
          </Button>
        </>
      )}
    </div>
  );
};
